import { z } from 'zod';
import {
    type MediaObject,
    mediaObjectSchema,
} from '../src/media.util';
import {
    type ProcessDataElementCollectionKindEnum,
    type ProcessDataElementKindEnum,
    type ProcessProcessStatusEnum,
} from './db';

const baseWorkInstructionStepAdvancedConfigConditionSchema = z.object({
    id: z.string(),
    kind: z.string(),
});

const manualWorkInstructionStepAdvancedConfigConditionSchema = baseWorkInstructionStepAdvancedConfigConditionSchema.extend({
    kind: z.literal('manual'),
});

const processStatusesRecord: {
    [key in `${ProcessProcessStatusEnum}`]: true;
} = {
    completed: true,
    failed: true,
    incomplete: true,
    manually_completed: true,
    not_required: true,
};

const processStatuses = Object.keys(processStatusesRecord) as [`${ProcessProcessStatusEnum}`, ...`${ProcessProcessStatusEnum}`[]];
const processStatusWorkInstructionStepAdvancedConfigConditionSchema = baseWorkInstructionStepAdvancedConfigConditionSchema.extend({
    processId: z.string(),
    kind: z.literal('processStatus'),
    status: z.enum(processStatuses),
});

export type ProcessStatusWorkInstructionStepAdvancedConfigCondition = z.infer<typeof processStatusWorkInstructionStepAdvancedConfigConditionSchema>;

const timerWorkInstructionStepAdvancedConfigConditionSchema = baseWorkInstructionStepAdvancedConfigConditionSchema.extend({
    kind: z.literal('timer'),
    duration: z.number().int().nonnegative(),
    unit: z.enum([ 'seconds', 'minutes', 'hours' ]),
});
export type TimerWorkInstructionStepAdvancedConfigCondition = z.infer<typeof timerWorkInstructionStepAdvancedConfigConditionSchema>;

const workInstructionStepAdvancedConfigConditionSchema = z.discriminatedUnion('kind', [
    manualWorkInstructionStepAdvancedConfigConditionSchema,
    processStatusWorkInstructionStepAdvancedConfigConditionSchema,
    timerWorkInstructionStepAdvancedConfigConditionSchema,
]);
export type WorkInstructionStepAdvancedConfigCondition = z.infer<typeof workInstructionStepAdvancedConfigConditionSchema>;

const workInstructionStepAdvancedConfigSchema = z.object({
    kind: z.enum([ 'any', 'all' ]),
    conditions: z.array(workInstructionStepAdvancedConfigConditionSchema),
});

const alwaysShowConditionSchema = z.object({
    kind: z.literal('always'),
});

const processCountShowConditionSchema = z.object({
    count: z.number().int().nonnegative(),
    processId: z.string(),
    kind: z.literal('processCount'),
    operator: z.enum([ '=', '>', '<', '>=', '<=', '!=' ]),
});
export type ProcessCountShowCondition = z.infer<typeof processCountShowConditionSchema>;

const processStatusShowConditionSchema = z.object({
    processId: z.string(),
    kind: z.literal('processStatus'),
    status: z.enum(processStatuses),
});

const baseAnnotationSchema = z.object({
    id: z.string(),
    kind: z.string(),
    color: z.string(),
    angle: z.number(),
    strokeWidth: z.number(),
    showCondition: z.discriminatedUnion('kind', [
        alwaysShowConditionSchema,
        processCountShowConditionSchema,
        processStatusShowConditionSchema,
    ]),
    opacity: z.object({
        percent: z.number().int().nonnegative(),
        animationDuration: z.number().int().nonnegative(),
    }),
});

const rectangleAnnotationSchema = baseAnnotationSchema.extend({
    kind: z.literal('rectangle'),
    topLeft: z.object({
        x: z.number(),
        y: z.number(),
    }),
    bottomRight: z.object({
        x: z.number(),
        y: z.number(),
    }),
});
export type RectangleAnnotation = z.infer<typeof rectangleAnnotationSchema>;

const arrowAnnotationSchema = baseAnnotationSchema.extend({
    kind: z.literal('arrow'),
    end: z.object({
        x: z.number(),
        y: z.number(),
    }),
    start: z.object({
        x: z.number(),
        y: z.number(),
    }),
});
export type ArrowAnnotation = z.infer<typeof arrowAnnotationSchema>;

const ellipseAnnotationSchema = baseAnnotationSchema.extend({
    kind: z.literal('ellipse'),
    topLeft: z.object({
        x: z.number(),
        y: z.number(),
    }),
    bottomRight: z.object({
        x: z.number(),
        y: z.number(),
    }),
});
export type EllipseAnnotation = z.infer<typeof ellipseAnnotationSchema>;

const textAnnotationSchema = baseAnnotationSchema.extend({
    kind: z.literal('text'),
    content: z.string(),
    start: z.object({
        x: z.number(),
        y: z.number(),
    }),
    fontFamily: z.enum([ 'Arial', 'Courier New', 'Times New Roman', 'Verdana', 'Tahoma', 'Georgia' ]),
});

export type TextAnnotation = z.infer<typeof textAnnotationSchema>;

export const workInstructionAnnotationSchema = z.discriminatedUnion('kind', [
    arrowAnnotationSchema,
    textAnnotationSchema,
    ellipseAnnotationSchema,
    rectangleAnnotationSchema,
]);
export type WorkInstructionAnnotation = z.infer<typeof workInstructionAnnotationSchema>;

const workInstructionOutputValue = z.union([ z.string(), z.number(), z.boolean() ]);
export type WorkInstructionOutputValue = z.infer<typeof workInstructionOutputValue>;

const workInstructionOutputConfigValueSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('static'),
        value: workInstructionOutputValue.nullable().default(null),
    }),
    z.object({
        processParamId: z.string(),
        source: z.literal('processParam'),
    }),
]);
export type WorkInstructionOutputConfigValue = z.infer<typeof workInstructionOutputConfigValueSchema>;

const workInstructionStepSchema = z.object({
    id: z.string(),
    name: z.string(),
    stepNum: z.number().int().nonnegative(),
    workInstruction: z.string(),
    linkedProcessIds: z.array(z.string()),
    linkedMedia: mediaObjectSchema.nullable(),
    annotations: z.array(workInstructionAnnotationSchema),
    advanceConfig: workInstructionStepAdvancedConfigSchema,
    outputs: z.record(workInstructionOutputConfigValueSchema),
});
export type WorkInstructionStep = z.infer<typeof workInstructionStepSchema>;

export const workInstructionOutputSchema = z.object({
    idx: z.number(),
    name: z.string(),
    defaultValue: workInstructionOutputValue,
});

export const workInstructionVersionSchema = z.object({
    steps: z.array(workInstructionStepSchema),
    // using applet variable value schema to make it easier to integrate these outputs with applets
    outputs: z.record(workInstructionOutputSchema).default({}),
});
export type WorkInstructionVersion = z.infer<typeof workInstructionVersionSchema>;

// common type shared between api and binding manager for calling the work instruction get or create route
export const getOrCreateWorkInstructionInstanceInputsSchema = z.object({
    workInstructionVersionId: z.string().uuid(),
    entity: z.object({
        id: z.string(),
        locationId: z.string().uuid().optional(),
        kind: z.union([ z.literal('material'), z.literal('asset') ]),
    }).nullable(),
});

export type GetOrCreateWorkInstructionInstanceInputs = z.infer<typeof getOrCreateWorkInstructionInstanceInputsSchema>;

/**
 * Codegen type for tracking the completion progress of a work instruction instance step.
 * if the step does not use a category of completion, the value will be null.
 */
export type WorkInstructionInstanceStepCompletionProgress = {
    timerCondIsCompleted: boolean | null;
    manualCondIsCompleted: boolean | null;
    processesCondIsCompleted: boolean | null;
}

type BaseWorkInstructionUpdatePayload = {
    kind: string;
}

export type StepUpdateProcessStatus = {
    processId: string;
    // NOTE: required count can be null if the process solve failed and no spec was choosen
    requiredCount: number | null;
    failedSteps: number;
    passedSteps: number;
    processName: string;
    status: ProcessProcessStatusEnum;
    processParams: {
        name: string;
        paramId: string;
        value: string | boolean | number;
        kind: ProcessDataElementKindEnum;
        collection_kind: ProcessDataElementCollectionKindEnum.SpecParam;
    }[];
};

export interface StepUpdatePayload extends BaseWorkInstructionUpdatePayload {
    stepNum: number;
    stepName: string;
    kind: 'stepUpdate';
    instanceStepId: string;
    instructionText: string;
    totalStepCount: number;
    media: MediaObject | null;
    workInstructionName: string;
    workInstructionVersion: number;
    /** whether this step includes a manual advance condition */
    eventToCompleteIncluded: boolean;
    workInstructionInstanceId: string;
    processStatuses: StepUpdateProcessStatus[];
    mediaAnnotations: WorkInstructionAnnotation[];
    stepIdFromWorkInstructionVersionConfig: string;
    stepOutputs: Record<string, NonNullable<WorkInstructionOutputValue>>;
}

interface WorkInstructionCompletedPayload extends BaseWorkInstructionUpdatePayload {
    kind: 'completed';
}

interface WorkInstructionLoadingPayload extends BaseWorkInstructionUpdatePayload {
    kind: 'loading';
}

// This is the type that gets emitted from api to runtime
export type WorkInstructionUpdatePayload = StepUpdatePayload | WorkInstructionCompletedPayload | WorkInstructionLoadingPayload;

// Avoids littering these kinds of checks elsewhere: `stepData.kind === 'completed' || stepData.kind === 'loading'`
export const isNonRenderableWorkInstructionKind = (payload: WorkInstructionUpdatePayload): payload is (WorkInstructionCompletedPayload | WorkInstructionLoadingPayload) => {
    // Not returning a boolean, so that the type narrowing works in the calling code
    return [ 'completed', 'loading' ].includes(payload.kind);
};

/**
 * Begin dynamic input types for work instruction text
 */

export const processMenuCategoriesSchema = z.enum([ 'Name', 'Status', 'Current Count', 'Required Count' ]);
export type ProcessMenuCategories = z.infer<typeof processMenuCategoriesSchema>;

export const processOutputCategorySchema = z.enum([ 'Process Parameter', 'Process Output' ]);
export type ProcessOutputCategory = z.infer<typeof processOutputCategorySchema>;

export type ContextMenuMarkdownCategory = {
    subItems: ContextMenuMarkdownCategory[];
    label: string | ProcessMenuCategories;
}

export const topLevelContextMenuCategoriesSchema = z.enum([ 'Processes' ]);
export type TopLevelContextMenuCategories = z.infer<typeof topLevelContextMenuCategoriesSchema>;
